import type { ChatProvider, ChatState, ChatMessage } from '../types';
import { MOCK_PRODUCTS } from '../../services/mockData';

/**
 * Demo chat provider for SPEC-030 demo mode.
 * Implements ChatProvider interface with local message handling and simulated advisor responses.
 * Zero external dependencies -- no Twilio SDK.
 */

/**
 * Simulated product card message using MOCK_PRODUCTS.
 * Uses Variant A format (attributes.type === 'product', attributes.json is stringified array).
 * Maps Product.title to 'name' to match Twilio product card format (SPEC-031/G6).
 */
const DEMO_PRODUCT_MESSAGE: ChatMessage = {
  id: 'demo-msg-products',
  author: 'Advisor',
  body: '',
  timestamp: new Date(),
  isLocal: false,
  attributes: {
    type: 'product',
    json: JSON.stringify(
      MOCK_PRODUCTS.map(product => ({
        id: product.id,
        name: product.title,      // Map title -> name for Twilio format
        price: product.price,
        image_url: product.image_url,
        url: null,
      }))
    ),
  },
};

const DEMO_RESPONSES = [
  'Welcome! I would be happy to help you explore our collection today.',
  'That is an excellent choice. Would you like to see it in a different color?',
  'I can arrange a private viewing for you. Shall I check availability?',
  'This piece is from our latest collection and is very popular.',
  'Let me know if you have any questions about sizing or materials.',
];

export class DemoChatProvider implements ChatProvider {
  readonly name = 'demo';

  private state: ChatState = {
    isConnected: false,
    isConnecting: false,
    messages: [],
    error: null,
    historyLoaded: false,
  };
  private subscribers: Set<(state: ChatState) => void> = new Set();
  private responseIndex = 0;
  private pendingTimers: ReturnType<typeof setTimeout>[] = [];

  getState(): ChatState {
    return { ...this.state };
  }

  subscribe(callback: (state: ChatState) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  async connect(_token: string, _conversationId: string): Promise<void> {
    this.updateState({ isConnecting: true, error: null });

    // Simulate connection delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Add welcome message from advisor
    const welcomeMessage: ChatMessage = {
      id: `demo-msg-welcome`,
      author: 'Advisor',
      body: 'Hello! Welcome to your personal consultation. How may I assist you today?',
      timestamp: new Date(),
      isLocal: false,
    };

    // Treat the welcome message as the loaded history baseline; the product
    // recommendations that arrive on the timer below are the live suggestion
    // (they should surface in the soft-focus panel — IB-004).
    this.updateState({
      isConnected: true,
      isConnecting: false,
      messages: [welcomeMessage],
      historyLoaded: true,
    });

    // After a brief delay, simulate advisor sharing product recommendations (SPEC-031/G6)
    const productTimer = setTimeout(() => {
      const productMsg: ChatMessage = {
        ...DEMO_PRODUCT_MESSAGE,
        id: `demo-msg-products-${Date.now()}`,
        timestamp: new Date(),
      };
      this.updateState({
        messages: [...this.state.messages, productMsg],
      });
    }, 2000);

    this.pendingTimers.push(productTimer);
  }

  async sendMessage(text: string, attributes?: Record<string, unknown>): Promise<void> {
    if (!text.trim() && !attributes) return;

    // Add local message immediately (include attributes for rich messages like product cards)
    const localMessage: ChatMessage = {
      id: `demo-msg-${Date.now()}`,
      author: 'You',
      body: text.trim(),
      timestamp: new Date(),
      isLocal: true,
      attributes,
    };

    this.updateState({
      messages: [...this.state.messages, localMessage],
    });

    // Simulate advisor response only for text messages (not attribute-only rich messages)
    if (text.trim()) {
      const delay = 1000 + Math.random() * 2000;
      const timer = setTimeout(() => {
        const responseText = DEMO_RESPONSES[this.responseIndex % DEMO_RESPONSES.length];
        this.responseIndex++;

        const responseMessage: ChatMessage = {
          id: `demo-msg-response-${Date.now()}`,
          author: 'Advisor',
          body: responseText,
          timestamp: new Date(),
          isLocal: false,
        };

        this.updateState({
          messages: [...this.state.messages, responseMessage],
        });
      }, delay);

      this.pendingTimers.push(timer);
    }
  }

  disconnect(): void {
    // Clear all pending timers to prevent memory leaks (AC-12)
    this.pendingTimers.forEach(timer => clearTimeout(timer));
    this.pendingTimers = [];

    this.updateState({
      isConnected: false,
      messages: [],
      historyLoaded: false,
    });
  }

  private updateState(partial: Partial<ChatState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  private notify(): void {
    const state = this.getState();
    this.subscribers.forEach(cb => cb(state));
  }
}
